import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import ConfirmDialog from '../components/modals/ConfirmDialog';
import { 
  ArrowLeft, 
  Edit, 
  Trash2, 
  CheckCircle, 
  XCircle,
  FileText,
  Calendar,
  DollarSign,
  User,
  Clock
} from 'lucide-react';
import { apiService } from '../services/api';
import { CreditMemoDto } from '../types';

const CreditMemoDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Confirmation modal state
  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // Fetch credit memo details
  const { data: creditMemo, isLoading, error } = useQuery({
    queryKey: ['credit-memo', id],
    queryFn: () => apiService.getCreditMemo(parseInt(id!)),
    enabled: !!id
  });

  // Apply credit memo mutation
  const applyCreditMemoMutation = useMutation({
    mutationFn: () => apiService.applyCreditMemo(parseInt(id!)),
    onSuccess: () => {
      toast.success('Credit memo applied successfully');
      queryClient.invalidateQueries({ queryKey: ['credit-memo', id] });
      queryClient.invalidateQueries({ queryKey: ['credit-memos'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      setIsApplyModalOpen(false);
    },
    onError: (error: any) => {
      toast.error('Failed to apply credit memo');
      console.error('Error applying credit memo:', error);
    }
  });

  // Cancel credit memo mutation
  const cancelCreditMemoMutation = useMutation({
    mutationFn: () => apiService.cancelCreditMemo(parseInt(id!)),
    onSuccess: () => {
      toast.success('Credit memo cancelled successfully');
      queryClient.invalidateQueries({ queryKey: ['credit-memo', id] });
      queryClient.invalidateQueries({ queryKey: ['credit-memos'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      setIsCancelModalOpen(false);
    },
    onError: (error: any) => {
      toast.error('Failed to cancel credit memo');
      console.error('Error cancelling credit memo:', error);
    }
  });

  // Delete credit memo mutation
  const deleteCreditMemoMutation = useMutation({
    mutationFn: () => apiService.deleteCreditMemo(parseInt(id!)),
    onSuccess: () => {
      toast.success('Credit memo deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['credit-memos'] });
      setIsDeleteModalOpen(false);
      navigate('/credit-memos');
    },
    onError: (error: any) => {
      toast.error('Failed to delete credit memo');
      console.error('Error deleting credit memo:', error);
    }
  });

  const handleApplyCreditMemo = () => {
    setIsApplyModalOpen(true);
  };

  const handleCancelCreditMemo = () => {
    setIsCancelModalOpen(true);
  };

  const handleDeleteCreditMemo = () => {
    setIsDeleteModalOpen(true);
  };

  const confirmApply = () => {
    applyCreditMemoMutation.mutate();
  };

  const confirmCancel = () => {
    cancelCreditMemoMutation.mutate();
  };

  const confirmDelete = () => {
    deleteCreditMemoMutation.mutate();
  };

  const getStatusBadge = (status: string) => {
    const statusClasses = {
      'Draft': 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
      'Applied': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      'Cancelled': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
    };

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusClasses[status as keyof typeof statusClasses] || statusClasses.Draft}`}>
        {status}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-color-600"></div>
      </div>
    );
  }

  if (error || !creditMemo) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">Error loading credit memo</div>
          <div className="text-gray-600 dark:text-gray-400">{error?.message || 'Credit memo not found'}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/credit-memos')}
                className="mr-4 p-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                  {creditMemo.creditMemoNumber}
                </h1>
                <p className="mt-2 text-gray-600 dark:text-gray-400">
                  Credit memo for invoice {creditMemo.originalInvoiceNumber}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {getStatusBadge(creditMemo.status)}
              {creditMemo.status === 'Draft' && (
                <>
                  <button
                    onClick={() => navigate(`/credit-memos/${creditMemo.id}/edit`)}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit
                  </button>
                  <button
                    onClick={handleApplyCreditMemo}
                    disabled={applyCreditMemoMutation.isPending}
                    className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Apply
                  </button>
                  <button
                    onClick={handleDeleteCreditMemo}
                    disabled={deleteCreditMemoMutation.isPending}
                    className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </button>
                </>
              )}
              {creditMemo.status === 'Applied' && (
                <button
                  onClick={handleCancelCreditMemo}
                  disabled={cancelCreditMemoMutation.isPending}
                  className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 disabled:opacity-50"
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Cancel
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Credit Memo Items */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Credit Memo Items</h2>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Qty
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Rate
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Tax
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {creditMemo.items.map((item, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4">
                          <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {item.description}
                            </div>
                            {item.notes && (
                              <div className="text-sm text-gray-500 dark:text-gray-400">
                                {item.notes}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {item.quantity} {item.unit}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          ${item.rate.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          ${item.amount.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {item.isTaxable ? 'Yes' : 'No'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Notes */}
            {creditMemo.notes && (
              <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
                <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Notes</h2>
                <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                  {creditMemo.notes}
                </p>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Credit Memo Summary */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Summary</h2>
              
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Subtotal:</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    ${creditMemo.amount.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Tax:</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    ${creditMemo.taxAmount.toFixed(2)}
                  </span>
                </div>
                <div className="border-t border-gray-200 dark:border-gray-600 pt-3">
                  <div className="flex justify-between">
                    <span className="text-base font-medium text-gray-900 dark:text-white">Total:</span>
                    <span className="text-base font-bold text-gray-900 dark:text-white">
                      ${creditMemo.total.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Credit Memo Information */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Information</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Credit Memo Number
                  </label>
                  <div className="text-sm text-gray-900 dark:text-white font-medium">
                    {creditMemo.creditMemoNumber}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Original Invoice
                  </label>
                  <div className="text-sm text-gray-900 dark:text-white">
                    {creditMemo.originalInvoiceNumber}
                  </div>
                </div>
                
                {creditMemo.reason && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Reason
                    </label>
                    <div className="text-sm text-gray-900 dark:text-white">
                      {creditMemo.reason}
                    </div>
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Status
                  </label>
                  <div>
                    {getStatusBadge(creditMemo.status)}
                  </div>
                </div>
              </div>
            </div>

            {/* Timeline */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Timeline</h2>
              
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="h-8 w-8 bg-accent-color-100 dark:bg-accent-color-900 rounded-full flex items-center justify-center">
                      <FileText className="h-4 w-4 text-accent-color-600 dark:text-accent-color-400" />
                    </div>
                  </div>
                  <div className="ml-3">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      Credit memo created
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {new Date(creditMemo.createdAt).toLocaleString()}
                    </div>
                    {creditMemo.createdBy && (
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        by {creditMemo.createdBy}
                      </div>
                    )}
                  </div>
                </div>
                
                {creditMemo.appliedAt && (
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="h-8 w-8 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                        <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                      </div>
                    </div>
                    <div className="ml-3">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        Credit memo applied
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {new Date(creditMemo.appliedAt).toLocaleString()}
                      </div>
                      {creditMemo.appliedBy && (
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          by {creditMemo.appliedBy}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {creditMemo.updatedAt && (
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="h-8 w-8 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                        <Clock className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                      </div>
                    </div>
                    <div className="ml-3">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        Last updated
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {new Date(creditMemo.updatedAt).toLocaleString()}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Apply Confirmation Modal */}
      <ConfirmDialog
        isOpen={isApplyModalOpen}
        onClose={() => setIsApplyModalOpen(false)}
        onConfirm={confirmApply}
        title="Apply Credit Memo"
        message={`Are you sure you want to apply credit memo "${creditMemo?.creditMemoNumber}"?`}
        confirmText="Apply"
        cancelText="Cancel"
        variant="info"
        isLoading={applyCreditMemoMutation.isPending}
      />

      {/* Cancel Confirmation Modal */}
      <ConfirmDialog
        isOpen={isCancelModalOpen}
        onClose={() => setIsCancelModalOpen(false)}
        onConfirm={confirmCancel}
        title="Cancel Credit Memo"
        message={`Are you sure you want to cancel credit memo "${creditMemo?.creditMemoNumber}"?`}
        confirmText="Cancel Credit Memo"
        cancelText="Keep Active"
        variant="warning"
        isLoading={cancelCreditMemoMutation.isPending}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmDialog
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Credit Memo"
        message={`Are you sure you want to delete credit memo "${creditMemo?.creditMemoNumber}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={deleteCreditMemoMutation.isPending}
      />
    </div>
  );
};

export default CreditMemoDetailsPage;
